
import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export interface CategoryOption {
  id: number;
  name: string;
}

interface CategorySelectProps {
  categories?: CategoryOption[];
  /** "all" or string id */
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  allLabel: string;
  triggerClassName?: string;
}

/**
 * Renders a category picker that uses a regular Select on desktop and a
 * bottom Sheet on mobile so the long category list stays comfortably
 * scrollable on small screens.
 */
export function CategorySelect({
  categories,
  value,
  onValueChange,
  placeholder,
  allLabel,
  triggerClassName,
}: CategorySelectProps) {
  const items = useMemo(() => {
    const m: Record<string, string> = { all: allLabel };
    categories?.forEach((cat) => {
      m[String(cat.id)] = cat.name;
    });
    return m;
  }, [categories, allLabel]);

  const currentLabel =
    value && value !== "all" ? items[value] ?? value : allLabel;

  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Desktop: regular Select */}
      <div className={cn("hidden md:block", triggerClassName)}>
        <Select
          items={items}
          value={value}
          onValueChange={(v) => onValueChange(v ?? "all")}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={placeholder ?? allLabel} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{allLabel}</SelectItem>
            {categories?.map((cat) => (
              <SelectItem key={cat.id} value={String(cat.id)}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile: bottom sheet trigger */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-3.5 text-sm font-normal text-foreground transition-colors hover:bg-muted/40 md:hidden",
          triggerClassName,
        )}
      >
        <span className={cn("truncate", value === "all" && "text-muted-foreground")}>
          {currentLabel}
        </span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </button>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[80vh] rounded-t-2xl"
        >
          <SheetHeader>
            <SheetTitle>{placeholder ?? allLabel}</SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-1 overflow-y-auto px-2 pb-6">
            <button
              type="button"
              onClick={() => {
                onValueChange("all");
                setMobileOpen(false);
              }}
              className={cn(
                "flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted",
                value === "all" && "bg-secondary text-secondary-foreground",
              )}
            >
              {allLabel}
              {value === "all" && <Check className="size-4 text-primary" />}
            </button>
            {categories?.map((cat) => {
              const v = String(cat.id);
              const active = value === v;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    onValueChange(v);
                    setMobileOpen(false);
                  }}
                  className={cn(
                    "flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted",
                    active && "bg-secondary text-secondary-foreground",
                  )}
                >
                  {cat.name}
                  {active && <Check className="size-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
